<template>
  <div>
    <div v-if="has_background_image" :class="backgroundClass" :style="main_bg_color_gradient" @error="handleImageError">
    </div>
    <div v-else :class="backgroundClass" :style="backgroundStyle" @error="handleImageError"></div>

    <b-navbar :class="[
      'cover-navbar',
      { 'has-background-image': has_background_image, scrolled: isScrolled },
    ]" variant="none">
      <b-container class="px-0">
        <b-navbar-brand @click="handleLogoClick">
          <b-img v-if="!theme_option.is_dark_option" :src="logoUrl" class="report-logo-sm logo-max-height"
            style="cursor: pointer" @error="handleLogoError" />
        </b-navbar-brand>

        <b-navbar-toggle target="nav-collapse"></b-navbar-toggle>

        <b-collapse id="nav-collapse" is-nav>
          <b-navbar-nav class="d-none d-sm-flex">
            <b-nav-item v-if="active_gri_table" :class="{ 'button-active': show_gri_table }"
              :variant="show_gri_table ? 'dark-border-selected' : 'dark-border'" class="px-2 py-0 item-button"
              @click="toggleTable('GRI')" :disabled="show_gri_table">
              <b-nav-text :style="{ color: cover ? dynamicTextColor : iconColor }" class="py-0">
                GRI
              </b-nav-text>
            </b-nav-item>
            <b-nav-item v-if="active_sasb_table" :class="{ 'button-active': show_sasb_table }"
              :variant="show_sasb_table ? 'dark-border-selected' : 'dark-border'" class="px-2 py-0 item-button"
              @click="toggleTable('SASB')" :disabled="show_sasb_table">
              <b-nav-text :style="{ color: cover ? dynamicTextColor : iconColor }" class="py-0">
                SASB
              </b-nav-text>
            </b-nav-item>
            <b-nav-item v-if="active_tcfd_table" :class="{ 'button-active': show_tcfd_table }"
              :variant="show_tcfd_table ? 'dark-border-selected' : 'dark-border'" class="px-2 py-0 item-button"
              @click="toggleTable('TCFD')" :disabled="show_tcfd_table">
              <b-nav-text :style="{ color: cover ? dynamicTextColor : iconColor }" class="py-0">
                TCFD
              </b-nav-text>
            </b-nav-item>
            <b-nav-item v-if="active_dc_table" :class="{ 'button-active': show_dc_table }"
              :variant="show_dc_table ? 'dark-border-selected' : 'dark-border'" class="px-2 py-0 item-button"
              @click="toggleTable('Data Center')" :disabled="show_dc_table">
              <b-nav-text :style="{ color: cover ? dynamicTextColor : iconColor }" class="py-0">
                Data Center
              </b-nav-text>
            </b-nav-item>
          </b-navbar-nav>

          <b-navbar-nav class="ml-auto d-none d-md-flex">
            <b-nav-item v-if="theme_option.show_translation_btn" @click="toggleLang" v-b-tooltip.hover
              :title="translationTitle" class="mr-2 item-button">
              <fa icon="language" size="lg" :style="{ color: cover ? dynamicTextColor : iconColor }" />
            </b-nav-item>
            <b-nav-item v-if="!is_viewer" @click="$router.push({ name: 'CheckReport' })" v-b-tooltip.hover
              :title="lang != 'ENG' ? '미리보기 닫기' : 'Close preview'" class="mr-2 item-button">
              <fa icon="times" size="lg" :style="{ color: cover ? dynamicTextColor : iconColor }" />
            </b-nav-item>
          </b-navbar-nav>

          <b-navbar-nav class="d-sm-none">
            <b-dropdown no-caret variant="none">
              <template #button-content>
                <fa icon="list" size="lg" :style="{ color: iconColor }" />
              </template>
              <b-dropdown-item :class="{ 'button-active': show_gri_table }" @click="toggleTable('GRI')"
                :style="{ color: dynamicTextColor }">
                GRI
              </b-dropdown-item>
              <b-dropdown-item :class="{ 'button-active': show_sasb_table }" @click="toggleTable('SASB')"
                :style="{ color: dynamicTextColor }">
                SASB
              </b-dropdown-item>
              <b-dropdown-item :class="{ 'button-active': show_tcfd_table }" @click="toggleTable('TCFD')"
                :style="{ color: dynamicTextColor }">
                TCFD
              </b-dropdown-item>
              <b-dropdown-item :class="{ 'button-active': show_dc_table }" @click="toggleTable('Data Center')" :style="{
                color: dynamicTextColor,
              }">
                Data Center
              </b-dropdown-item>
            </b-dropdown>
          </b-navbar-nav>

          <b-navbar-nav class="ml-auto d-md-none">
            <b-dropdown right no-caret variant="none">
              <template #button-content>
                <fa icon="list" size="lg" :style="{ color: iconColor }" />
              </template>
              <b-dropdown-item v-if="theme_option.show_translation_btn" @click="toggleLang" v-b-tooltip.hover
                :title="translationTitle" :style="{ color: dynamicTextColor }">
                <fa icon="language" size="lg" :style="{ color: dynamicTextColor }" />
                {{ translationTitle }}
              </b-dropdown-item>
              <b-dropdown-item v-if="!is_viewer" @click="$router.push({ name: 'CheckReport' })"
                v-b-tooltip.hover :title="lang != 'ENG' ? '미리보기 닫기' : 'Close preview'"
                :style="{ color: dynamicTextColor }">
                <fa icon="times" size="lg" :style="{ color: dynamicTextColor }" /> 미리보기 닫기
              </b-dropdown-item>
            </b-dropdown>
          </b-navbar-nav>
        </b-collapse>
      </b-container>
    </b-navbar>

    <b-container class="p-0">
      <div v-if="cover" class="main-cover">
        <h1 :style="titleStyle">
          {{ lang !== 'ENG' ? selected_report.report_name : selected_report.report_name_e }}
        </h1>
      </div>
      <div v-else class="sub-cover" :style="{ color: dynamicTextColor }" ref="subCover">
        <h1 :style="titleStyle">
          {{ reportTitle }}
        </h1>
        <ArrowButton />
      </div>
    </b-container>

    <div ref="contentsListWrapper">
      <contents-list-new v-if="isContentsListVisible"
        :categoryType="categoryType"></contents-list-new>
    </div>

    <div v-if="cover" class="main-under">
      <b-container class="px-0">
        <div class="button-container">
          <template v-if="active_sasb_table && !active_gri_table && !active_tcfd_table && !active_dc_table">
            <div v-for="(industry, key) in sasb_contents" :key="key" class="table-button"
              @click="handleCategoryClick(key)">
              <h4 class="navbar-text" :style="{ color: dynamicTextColor }">
                {{ industry.industry_name }} <b-icon icon="chevron-right" class="ml-2"></b-icon>
              </h4>
            </div>
          </template>

          <template v-else-if="
            active_tcfd_table && !active_gri_table && !active_sasb_table && !active_dc_table
          ">
            <div class="table-button" @click="handleCategoryClick('tcfd')">
              <h4 class="navbar-text" :style="{ color: dynamicTextColor }">
                TCFD <b-icon icon="chevron-right" class="ml-2"></b-icon>
              </h4>
            </div>
          </template>

          <template v-else-if="activeTables.length > 1">
            <div v-for="table in activeTables" :key="table.name" :class="{ 'table-button': true }"
              @click="toggleTable(table.name)">
              <h4 class="navbar-text" :style="{ color: dynamicTextColor }">
                {{ table.label }} <b-icon icon="chevron-right" class="ml-2"></b-icon>
              </h4>
            </div>
          </template>

          <template v-else>
            <div v-for="category in report_categories" :key="category.category_id" :class="{ 'table-button': true }"
              @click="handleCategoryClick(category.category_id)">
              <h4 class="navbar-text" :style="{ color: dynamicTextColor }">
                {{ category.category_name }} <b-icon icon="chevron-right" class="ml-2"></b-icon>
              </h4>
            </div>
          </template>
        </div>
      </b-container>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters, mapMutations, mapActions } from 'vuex';
import ContentsListNew from './contents_list_new/ContentsListNew.vue';
import ArrowButton from './contents_list_new/ArrowButton.vue';
import { updateParamsFromQueryString } from '@/components/util';

export default {
  name: 'ViewerHeaderCover',
  components: {
    ContentsListNew,
    ArrowButton,
  },
  props: {
    theme_option: {
      type: Object,
      default: () => ({}),
    },
    dark_logo_url: {
      type: [String, Boolean],
      default: null,
    },
    main_bg_color_gradient: {
      type: [Object, String],
      default: () => ({}),
    },
    content_list_open: {
      type: Boolean,
      default: false,
    },
    active_gri_table: {
      type: Boolean,
      default: false,
    },
    active_sasb_table: {
      type: Boolean,
      default: false,
    },
    active_tcfd_table: {
      type: Boolean,
      default: false,
    },
    active_dc_table: {
      type: Boolean,
      default: false,
    },
    show_gri_table: {
      type: Boolean,
      default: false,
    },
    show_sasb_table: {
      type: Boolean,
      default: false,
    },
    show_tcfd_table: {
      type: Boolean,
      default: false,
    },
    show_dc_table: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isContentsListVisible: false,
      cover: true,
      isScrolled: false,
      isBrightBackground: false,
      initialCategories: [],
      gri_categories_loaded: false,
      sasb_categories_loaded: false,
      selectedIndustryKey: null,
      categoryTypes: ['gri', 'sasb', 'tcfd', 'data center'],
      defaultLogoUrl: '/static_img/examples/logos/QESG.png',
      defaultBackgroundUrl: '/static_img/examples/default_background.jpg',
      img: '/static_img/examples/default_background.jpg',
      has_background_image: !!this.main_bg_color_gradient['background-image'],
    };
  },
  computed: {
    ...mapState('report_contents', ['sasb_contents']),
    ...mapState('report_list', ['lang']),
    ...mapGetters(['is_viewer']),
    ...mapGetters('report_list', ['selected_report', 'current_report_code']),
    ...mapGetters('gri2021_report_view', [
      'report_universal_categories',
      'report_topic_specific_categories',
    ]),
    ...mapGetters('category', ['selectedCategoryId']),
    main_color() {
      return this.selected_report.main_color;
    },
    dynamicTextColor() {
      if (this.has_background_image) {
        return this.isBrightBackground ? '#343434' : '#F4F4F4';
      }
      return '#343434';
    },
    backgroundStyle() {
      return this.has_background_image
        ? {
          backgroundImage: `url(${this.main_bg_color_gradient['background-image']})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }
        : {
          backgroundImage: `url(${this.defaultBackgroundUrl})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        };
    },
    logoUrl() {
      return this.theme_option.is_dark_option
        ? this.dark_logo_url || this.defaultLogoUrl
        : `${this.$store.state.static_host}${this.selected_report.logo_path}` ||
        this.defaultLogoUrl;
    },
    titleStyle() {
      return `font-size: ${this.theme_option?.title_size * 1.25}rem; font-family:${this.selected_report.font_family}; font-weight:700; color: ${this.theme_option?.title_color};`;
    },
    backgroundClass() {
      return this.cover
        ? 'report-basic-header-bg background-img'
        : 'report-basic-header-bg2 background-img';
    },
    iconColor() {
      if (this.isScrolled) {
        return '#343434';
      }
      return this.has_background_image ? '#343434' : '#343434';
    },
    printTitle() {
      return this.lang !== 'ENG' ? '출력하기' : 'Print to PDF';
    },
    translationTitle() {
      return this.lang !== 'ENG' ? '영문보기' : 'Korean';
    },
    reportTitle() {
      const tParam = this.$route.query.t
        ? this.$route.query.t.toLowerCase().replace(/%20/g, ' ')
        : '';
      switch (tParam) {
        case 'gri':
          return 'GRI';
        case 'sasb':
          return 'SASB';
        case 'tcfd':
          return 'TCFD';
        case 'data center':
          return 'Data Center';
        default:
          return '';
      }
    },
    report_categories() {
      if (this.categoryType === 'tcfd') {
        return [{ category_id: 'tcfd', category_name: 'TCFD' }];
      } else if (this.categoryType === 'data center') {
        return [{ category_id: 'data-center', category_name: 'Data Center' }];
      } else if (this.categoryType === 'sasb') {
        return Object.keys(this.sasb_contents).map((key) => ({
          category_id: key,
          category_name: this.sasb_contents[key]?.industry_name || '',
          category_name_e: this.sasb_contents[key]?.industry_name || '',
        }));
      } else if (this.categoryType === 'gri' && this.gri_categories_loaded) {
        let categories = [];
        if (this.report_universal_categories) {
          categories = categories.concat(
            this.report_universal_categories.map((category) => ({
              category_id: category.category_id,
              category_name: category.category_name,
              category_name_e: category.category_name_e,
            }))
          );
        }
        if (this.report_topic_specific_categories) {
          categories = categories.concat(
            this.report_topic_specific_categories.map((category) => ({
              category_id: category.category_id,
              category_name: category.category_name,
              category_name_e: category.category_name_e,
            }))
          );
        }
        return categories;
      } else {
        return [];
      }
    },
    activeTables() {
      const tables = [];
      if (this.active_gri_table)
        tables.push({ name: 'GRI', label: 'GRI', active: this.show_gri_table });
      if (this.active_sasb_table)
        tables.push({ name: 'SASB', label: 'SASB', active: this.show_sasb_table });
      if (this.active_tcfd_table)
        tables.push({ name: 'TCFD', label: 'TCFD', active: this.show_tcfd_table });
      if (this.active_dc_table)
        tables.push({ name: 'Data Center', label: 'Data Center', active: this.show_dc_table });
      return tables;
    },
  },
  methods: {
    ...mapActions('report_contents', ['fetchSelectedSasb']),
    ...mapMutations('report_list', ['change_lang']),
    ...mapActions('category', ['setSelectedCategoryId']),
    handleLogoClick() {
      window.location.reload();
    },
    handleLogoError(event) {
      event.target.src = this.defaultLogoUrl;
    },
    handleImageError(event) {
      console.error('Image loading error:', event);
      event.target.src = this.defaultBackgroundUrl;
    },
    setCoverTrue() {
      this.cover = true;
      this.isContentsListVisible = false;
      this.show_gri_table = false;
      this.show_sasb_table = false;
      this.show_tcfd_table = false;
      this.show_dc_table = false;
    },
    toggleTable(tableName) {
      this.cover = false;
      this.isContentsListVisible = true;
      this.categoryType = tableName.toLowerCase();

      if (this.categoryType === 'gri') {
        const griCategoryId =
          this.report_categories.length > 0 ? this.report_categories[0].category_id : undefined;
        const newQuery = {
          ...this.$route.query,
          t: this.categoryType,
          categoryId: griCategoryId,
          industry_key: undefined,
        };
        if (JSON.stringify(newQuery) !== JSON.stringify(this.$route.query)) {
          this.$router.push({ query: newQuery });
        }
        if (griCategoryId) {
          this.selectCategory(griCategoryId);
        }
      } else {
        const newQuery = {
          ...this.$route.query,
          t: this.categoryType,
          categoryId: undefined,
          industry_key: undefined,
        };
        if (JSON.stringify(newQuery) !== JSON.stringify(this.$route.query)) {
          this.$router.push({ query: newQuery });
        }
      }

      this.$emit('showTableView', tableName);
      this.loadInitialCategories();
    },
    toggleLang() {
        if (this.lang == 'KOR') {
          this.change_lang('ENG');
          document.querySelector('title').textContent = this.selected_report.report_name_e;
          updateParamsFromQueryString('l', 'ENG', false);
        } else {
          this.change_lang('KOR');
          document.querySelector('title').textContent = this.selected_report.report_name;
          updateParamsFromQueryString('l', null, false);
        }
      },
    toggleContentsList() {
      this.isContentsListVisible = !this.isContentsListVisible;
    },
    handleScroll() {
      if (this.$refs.subCover) {
        this.isScrolled = window.scrollY > 0;
      }

      if (this.$refs.contentsListWrapper && this.$refs.contentsListWrapper.firstChild) {
        const contentsListTop = this.$refs.contentsListWrapper.getBoundingClientRect().top;
        if (contentsListTop <= 80) {
          this.$refs.contentsListWrapper.firstChild.classList.add('fixed-position');
          this.$refs.contentsListWrapper.firstChild.classList.remove('border-top');
        } else {
          this.$refs.contentsListWrapper.firstChild.classList.remove('fixed-position');
        }
      }
    },
    handleCategoryClick(categoryId) {
      if (this.activeTables.length === 1) {
        this.toggleTable(this.activeTables[0].name);
      }
      this.selectCategory(categoryId);
    },
    getAverageRGB(imageElement) {
      const blockSize = 5;
      const canvas = document.createElement('canvas');
      const context = canvas.getContext && canvas.getContext('2d');
      let data, width, height;
      let i = -4;
      const rgb = { r: 0, g: 0, b: 0 };
      let count = 0;

      if (!context) {
        return { r: 0, g: 0, b: 0 };
      }

      height = canvas.height =
        imageElement.naturalHeight || imageElement.offsetHeight || imageElement.height;
      width = canvas.width =
        imageElement.naturalWidth || imageElement.offsetWidth || imageElement.width;

      context.drawImage(imageElement, 0, 0);

      try {
        data = context.getImageData(0, 0, width, height);
      } catch (error) {
        this.$Sentry.captureException(error);
        return { r: 0, g: 0, b: 0 };
      }

      const len = data.data.length;

      while ((i += blockSize * 4) < len) {
        ++count;
        rgb.r += data.data[i];
        rgb.g += data.data[i + 1];
        rgb.b += data.data[i + 2];
      }

      // ~~ used to floor values
      rgb.r = ~~(rgb.r / count);
      rgb.g = ~~(rgb.g / count);
      rgb.b = ~~(rgb.b / count);

      return rgb;
    },
    isBrightColor(rgb) {
      const yiq = (rgb.r * 299 + rgb.g * 587 + rgb.b * 114) / 1000;
      return yiq >= 64;
    },
    updateTextColorBasedOnBackground() {
      this.$nextTick(() => {
        this.img = new Image();
        this.img.crossOrigin = 'Anonymous';
        const imageUrl = this.main_bg_color_gradient['background-image']
          ? this.main_bg_color_gradient['background-image']
            .replace(/url\((['"])?(.*?)\1\)/gi, '$2')
            .split(',')[0] || '/static_img/examples/default_background.jpg'
          : '/static_img/examples/default_background.jpg';
        this.img.src = imageUrl;
        this.img.onload = () => {
          const rgb = this.getAverageRGB(this.img);
          this.isBrightBackground = this.isBrightColor(rgb);
        };
        this.img.onerror = (event) => {
          console.error('Image loading error:', event);
          this.has_background_image = false;
          this.img.src = '/static_img/examples/default_background.jpg';
          const rgb = this.getAverageRGB(this.img);
          this.isBrightBackground = this.isBrightColor(rgb);
        };
      });
    },
    selectCategory(categoryId) {
      this.setSelectedCategoryId(categoryId);
      let queryParam = {};
      if (this.categoryType === 'sasb') {
        queryParam = { industry_key: categoryId, categoryId: undefined };
      } else if (this.categoryType === 'gri') {
        queryParam = { categoryId: categoryId, industry_key: undefined };
      } else {
        queryParam = { categoryId };
      }
      const newQuery = { ...this.$route.query, ...queryParam };
      if (JSON.stringify(newQuery) !== JSON.stringify(this.$route.query)) {
        this.$router.push({ query: newQuery });
      }
    },
    loadInitialCategories() {
      let categories = [];
      if (this.report_universal_categories) {
        categories = categories.concat(
          this.report_universal_categories.map((category) => ({
            category_id: category.category_id,
            category_name: category.category_name,
            category_name_e: category.category_name,
          }))
        );
      }
      if (this.report_topic_specific_categories) {
        categories = categories.concat(
          this.report_topic_specific_categories.map((category) => ({
            category_id: category.category_id,
            category_name: category.category_name,
            category_name_e: category.category_name,
          }))
        );
      }
      this.initialCategories = categories;
      this.gri_categories_loaded = true;

      if (categories.length > 0 && this.categoryType === 'gri') {
        this.selectCategory(categories[0].category_id);
      }
    },
    loadIndustryKeyData(industryKey) {
      this.selectedIndustryKey = industryKey;
      if (this.sasb_categories_loaded) {
        this.selectedIndustryData = this.sasb_contents[industryKey];
      }
    },
    scrollLeft() {
      const currentIndex = this.report_categories.findIndex(
        (category) => category.category_id === this.selectedCategoryId
      );
      if (currentIndex > 0) {
        this.selectCategory(this.report_categories[currentIndex - 1].category_id);
      } else {
        this.switchCategoryType(-1);
      }
    },
    scrollRight() {
      const currentIndex = this.report_categories.findIndex(
        (category) => category.category_id === this.selectedCategoryId
      );
      if (currentIndex < this.report_categories.length - 1) {
        this.selectCategory(this.report_categories[currentIndex + 1].category_id);
      } else {
        this.switchCategoryType(1);
      }
    },
    switchCategoryType(direction) {
      const currentIndex = this.categoryTypes.indexOf(this.categoryType);
      const newIndex =
        (currentIndex + direction + this.categoryTypes.length) % this.categoryTypes.length;
      this.$router.push({
        query: {
          ...this.$route.query,
          t: this.categoryTypes[newIndex],
          categoryId: undefined,
          industry_key: undefined,
        },
      });
    },
  },
  watch: {
    sasb_contents(newList) {
      if (Object.keys(newList).length > 0) {
        this.sasb_categories_loaded = true;
        this.loadIndustryKeyData(this.selectedIndustryKey);
      }
    },
    '$route.query.t': {
      handler(newType) {
        this.categoryType = newType || 'gri';
        if (this.categoryType === 'sasb') {
          this.fetchSelectedSasb(this.current_report_code);
        } else {
          this.loadInitialCategories();
        }
      },
      immediate: true,
    },
    '$route.query.industry_key': {
      handler(newKey) {
        this.loadIndustryKeyData(newKey);
      },
      immediate: true,
    },
    selected_report(newVal) {
      if (newVal) {
        this.setCoverTrue();
        this.updateTextColorBasedOnBackground();
      }
    },
  },
  mounted() {
    this.fetchSelectedSasb(this.current_report_code);
    window.addEventListener('scroll', this.handleScroll);
  },
  created() {
    this.updateTextColorBasedOnBackground();
    this.loadInitialCategories();
  },
  beforeDestroy() {
    window.removeEventListener('scroll', this.handleScroll);
  },
};
</script>

<style lang="scss" scoped>
@import '~@fortawesome/fontawesome-free/css/all.css';

.report-basic-header {

  &-bg,
  &-bg2 {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    z-index: -1;
  }

  &-bg {
    height: 100vh;
  }

  &-bg2 {
    height: 40vh;
  }
}

.cover-navbar {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 80px;
  padding: 0;
  z-index: 999;
  transition:
    background-color 0.3s ease,
    backdrop-filter 0.3s ease;

  &.scrolled {
    background-color: rgba(255, 255, 255, 0.75);
    backdrop-filter: blur(10px);

    &::after {
      content: '';
      position: absolute;
      left: 0;
      right: 0;
      bottom: 0;
      height: 1px;
      background-color: lightgray;
    }

    .navbar-text,
    .b-nav-text,
    .fa,
    .b-nav-item {
      color: #000000cc !important;
    }
  }

  &::after {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    height: 1px;
    background-color: lightgray;
  }
}

.navbar-text {
  font-size: 1.25rem;
  font-weight: 700;
}

.logo-max-height {
  max-height: 60px;
}

.main-cover,
.sub-cover {
  display: flex;
  align-items: center;
  background-color: transparent;

  &.sub-cover {
    height: 40vh;
  }

  &.main-cover {
    height: 90vh;
  }
}

.main-under {
  height: 10vh;
  padding: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: transparent;

  .navbar-text {
    font-size: 1.5rem;
    font-weight: 700;
    color: rgba(0, 0, 0, 0.4);

    :hover,
    :focus,
    :active {
      color: rgba(0, 0, 0, 0.7);
    }
  }
}

.button-container {
  display: flex;
  width: 100%;
  flex-wrap: wrap;

  @media (max-width: 575.98px) {
    flex-direction: row;
    justify-content: space-between;
  }

  @media (min-width: 576px) {
    flex-direction: row;
    justify-content: start;
  }
}

.table-button {
  flex: 1 1 calc(50% - 10px);
  height: 10vh;
  background-color: transparent;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  transition: background-color 0.3s ease;
  color: rgba(0, 0, 0, 0.4);
  margin-bottom: 10px;

  &:hover {
    background-color: #e0e0e090;
  }

  @media (min-width: 576px) {
    flex: none;
    width: 20%;
    margin-bottom: 0;
  }
}

.report-basic-toc {
  position: relative;
  width: 100%;
  padding-top: 15px;
  padding-bottom: 15px;
  background-color: transparent;
}

.menu-container {
  display: flex;
  align-items: center;
  height: 50px;
}

.categories-wrapper {
  display: flex;
  flex-grow: 1;
  scroll-behavior: smooth;
}

.categories {
  display: flex;
  align-items: center;
  white-space: nowrap;
}

.category-button {
  margin-right: 1.5vw;
}

.button-active {
  background-color: #e0e0e090 !important;
  border-radius: 10px;
}

.fixed-position {
  position: fixed;
  top: 80px;
  width: 100%;
  z-index: 1000;
  background: white;
  background: rgba(255, 255, 255, 0.75);
  backdrop-filter: blur(10px);
  transition:
    background-color 0.3s ease,
    backdrop-filter 0.3s ease;
}

.item-button {
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  transition: background-color 0.3s ease;
  color: rgba(0, 0, 0, 0.7);
  margin-bottom: 10px;
  border-radius: 10px;

  &:hover {
    background-color: #e0e0e090;
    border-radius: 10px;
  }

  &.button-active {
    background-color: #e0e0e090 !important;
    border-radius: 10px;
  }
}
</style>
